import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@utils/cn';
import { Loading } from '@components/atoms/Loading';

export interface WizardStep {
  key: string;
  label: string;
}

interface WizardProps {
  steps: WizardStep[];
  currentStep: number;
  onStepChange?: (index: number) => void;
  /** Runs the current step's own validation; returning false blocks advancing. */
  onNext: () => Promise<boolean> | boolean;
  onBack?: () => void;
  nextLabel?: string;
  backLabel?: string;
  isBusy?: boolean;
  /** Hides the footer entirely (used by the Plan step once an option's own body takes over). */
  hideFooter?: boolean;
  children: React.ReactNode;
}

/**
 * Sprint 31, story-160. Generic stepper shell shared by `ProjectWizardPage` — header with a
 * clickable-back progress row, an animated step body (`framer-motion`, already a project
 * dependency — see `SlideOver`/`Layout`/`ProjectFeed`), and a footer nav.
 */
export const Wizard: React.FC<WizardProps> = ({
  steps,
  currentStep,
  onStepChange,
  onNext,
  onBack,
  nextLabel,
  backLabel,
  isBusy,
  hideFooter,
  children,
}) => {
  const [advancing, setAdvancing] = React.useState(false);

  const handleNext = async () => {
    setAdvancing(true);
    try {
      await onNext();
    } finally {
      setAdvancing(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-2 overflow-x-auto">
        {steps.map((step, index) => (
          <React.Fragment key={step.key}>
            {index > 0 && (
              <div
                className={cn(
                  'h-0.5 flex-1 min-w-[16px] rounded',
                  index <= currentStep ? 'bg-primary' : 'bg-gray-200 dark:bg-gray-700',
                )}
              />
            )}
            <button
              type="button"
              onClick={() => index < currentStep && onStepChange?.(index)}
              disabled={index >= currentStep}
              data-testid={`wizard-step-${step.key}`}
              className={cn(
                'flex items-center gap-1.5 whitespace-nowrap text-xs font-medium px-1 py-1 rounded',
                index === currentStep
                  ? 'text-primary'
                  : index < currentStep
                    ? 'text-gray-700 dark:text-gray-300 cursor-pointer'
                    : 'text-gray-400 dark:text-gray-600 cursor-default',
              )}>
              <span
                className={cn(
                  'w-2 h-2 rounded-full',
                  index <= currentStep ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600',
                )}
              />
              {step.label}
            </button>
          </React.Fragment>
        ))}
      </div>

      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={steps[currentStep]?.key}
          initial={{ opacity: 0, x: 12 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -12 }}
          transition={{ duration: 0.18 }}>
          {children}
        </motion.div>
      </AnimatePresence>

      {!hideFooter && (
        <div className="flex justify-between items-center pt-2 border-t border-gray-100 dark:border-gray-700">
          <div>
            {currentStep > 0 && onBack && (
              <button
                type="button"
                onClick={onBack}
                data-testid="wizard-back-button"
                className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white">
                {backLabel}
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={handleNext}
            disabled={advancing || isBusy}
            data-testid="wizard-next-button"
            className="px-5 py-2 rounded-lg text-sm font-medium bg-primary text-white disabled:opacity-60 flex items-center gap-2">
            {(advancing || isBusy) && <Loading size="sm" />}
            {nextLabel}
          </button>
        </div>
      )}
    </div>
  );
};

export default Wizard;
